import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, H1, Input, Field } from '../elements/index.js';
import { Colors } from '../Theme';
import FormUtils from '../utils/Form';

const Login = ({ children, error, loading, logo, onLogin, siteKey, showCaptcha, title }) => {
  const styles = {
    component: {
      minHeight: '100vh'
    },
    description: {
      margin: '10px 0 30px',
      fontSize: '13px',
      fontStyle: 'italic',
      color: '#777'
    },
    error: {
      background: `${Colors.RED.hex}25`,
      color: Colors.RED.hex,
      fontWeight: 600,
      padding: 15,
      borderRadius: 4,
      marginBottom: 20
    },
    header: {
      background: Colors.GRAY.hex,
      position: 'relative',
      height: 200,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    slant: {
      background: Colors.GRAY.hex,
      height: 200,
      position: 'absolute',
      bottom: 0,
      left: '-5%',
      right: '-5%',
      zIndex: 0,
      transform: 'rotate(-5deg) translateY(50%)'
    },
    logo: {
      marginBottom: 40,
      position: 'relative',
      zIndex: 2
    },
    login_wrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
      paddingTop: 80
    },
    login: {
      width: 384,
      background: '#fff',
      padding: 40,
      borderRadius: 8,
      marginTop: -100,
      zIndex: 4,
      border: '1px solid #eee',
      borderBottom: '3px solid #ddd'
    }
  };

  const [data, setData] = useState({
    email: '',
    password: ''
  });
  const [show_recaptcha, setShowRecaptcha] = useState(false);
  const [recaptcha_loaded, setRecaptchaLoaded] = useState(false);
  const [errors, setErrors] = useState([]);

  window.showRecaptcha = () => {
    setShowRecaptcha(true);
  };

  useEffect(() => {
    if (showCaptcha) {
      const existing_el = document.getElementById('recaptcha_script');

      if (!existing_el) {
        const el = document.createElement('script');

        el.src = 'https://www.google.com/recaptcha/api.js?onload=showRecaptcha&render=explicit';
        el.id = 'recaptcha_script';

        document.body.appendChild(el);
      }
    }

    return () => {
      if (showCaptcha) {
        const existing_el = document.getElementById('recaptcha_script');

        if (existing_el) {
          existing_el.parentNode.removeChild(existing_el);
        }
      }

      document.onkeypress = null;
    };
  }, []);

  document.onkeypress = e => {
    if (e.key === 'Enter') {
      login();
    }
  };

  const handleInputChange = (name, e) => {
    setData({ ...data, [name]: e.target.value });
  };

  const reCaptchaSuccess = () => {
    const new_errors = errors.filter(e => e !== 'recaptcha');

    setErrors(new_errors);
  };

  const reCaptchaError = () => {
    const new_errors = errors.filter(e => e !== 'recaptcha');

    new_errors.push('recaptcha');

    setErrors(new_errors);
  };

  const login = () => {
    const new_errors = [];

    if (!data.password || data.password.length < 6) {
      new_errors.push('password');
    }

    if (!FormUtils._validateEmail(data.email)) {
      new_errors.push('email');
    }

    setErrors(new_errors);

    if (new_errors.length) {
      if (showCaptcha) {
        window.grecaptcha.reset();
      }
    } else {
      onLogin({ email: data.email, password: data.password });
    }
  };

  const recaptchaDivLoaded = el => {
    if (!recaptcha_loaded && el) {
      setRecaptchaLoaded(true);

      window.grecaptcha.render(el, {
        sitekey: siteKey,
        callback: reCaptchaSuccess,
        'expired-callback': reCaptchaError,
        'error-callback': reCaptchaError
      });
    }
  };

  return (
    <div style={styles.component}>
      <div style={styles.header}>
        <div style={styles.logo}>{logo}</div>
        <div style={styles.slant} />
      </div>
      <div style={styles.login_wrapper}>
        <div style={styles.login}>
          <H1>
            {title}
            <p style={styles.description}>{children}</p>
          </H1>
          {error ? <div style={styles.error}>{error}</div> : null}
          <Field label='Email'>
            <Input
              invalid={errors.includes('email')}
              onChange={handleInputChange.bind(null, 'email')}
              required={true}
              type='text'
            />
          </Field>
          <Field label='Password'>
            <Input
              invalid={errors.includes('password')}
              onChange={handleInputChange.bind(null, 'password')}
              required={true}
              type='password'
            />
          </Field>
          {show_recaptcha ? <div ref={recaptchaDivLoaded} /> : null}
          <Button
            disabled={loading}
            icon={loading ? 'loading mdi-spin' : null}
            onClick={login}
            style={{ marginTop: 20 }}
          >
            {loading ? 'Authenticating...' : 'Sign In'}
          </Button>
        </div>
      </div>
    </div>
  );
};

Login.propTypes = {
  children: PropTypes.array,
  description: PropTypes.string,
  error: PropTypes.string,
  loading: PropTypes.bool,
  logo: PropTypes.node,
  onLogin: PropTypes.func,
  showCaptcha: PropTypes.bool,
  siteKey: PropTypes.string,
  title: PropTypes.string
};

export default Login;
